<template>
  <div class="customer-page">
    <template v-if="!restrictedBrand">
      <!-- Main Header -->
      <div class="main-header">
        <ulu-customer-header :web_app_id="customer.web_app_id" :brand="customer.brand" :name="customer.company_name"
          :registration_sent_at="customer.webapp_registration_sent_at"
          :registration_accepted_at="customer.webapp_registration_accepted_at" :company_country="customer.company_country"
          :contact_first_name="customer.contact_first_name" :contact_last_name="customer.contact_last_name"
          :contact_email="customer.contact_email" :refresh="fetchCustomer" />
        <button class="refresh-btn" @click="fetchCustomer" :disabled="getShowLoadingModal" v-b-tooltip.hover
          :title="$t('globals.reload_customer')">
          <v-icon name="sync-alt" :spin="getShowLoadingModal"></v-icon>
        </button>
      </div>

      <!-- Main Content Layout -->
      <div class="customer-content">
        <!-- Left Sidebar -->
        <div class="customer-sidebar">
          <div class="customer-number-container">
            <h2 class="customer-number">
              {{ $t("globals.customer").toUpperCase() + " " + (customer.id || "") }}
            </h2>
          </div>

          <ulu-customer-details :customer="customer" />
        </div>

        <!-- Main Content Area -->
        <div class="customer-main">
          <div class="tabs-wrapper">
            <b-tabs lazy v-model="tab">
              <!-- Customer data -->
              <b-tab :title="$t('globals.data')">
                <ulu-customer-data :customer="customer" :refresh="fetchCustomer" />
              </b-tab>
              <!-- Customer orders and inquiries -->
              <b-tab :title="$t('globals.orders_inquiries')">
                <ulu-customer-orders-inquiries :customer="customer.id"
                  :total="parseInt(customer.orders_total) + parseInt(customer.inquiries_total)"
                  :inquiries="customer.inquiries" />
              </b-tab>
              <!-- Customer devices -->
              <b-tab :title="$t('globals.devices')">
                <ulu-customer-devices :customer="customer.id" :web_app_id="customer.web_app_id" :total="parseInt(customer.devices_total)" />
              </b-tab>
              <!-- Customer invoices -->
              <b-tab :title="$t('globals.invoices')">
                <ulu-customer-invoices :customer="customer.id" :company_name="customer.company_name"
                  :currency="'EUR' /*customer.currency*/" :total="parseInt(customer.invoices_total)" />
              </b-tab>
              <!-- Customer parking invoices -->
              <b-tab :title="$t('globals.parking_invoices')">
                <ulu-customer-parking-invoices :total="parseInt(customer.parking_invoices_total)" :customer="customer.id"
                  :currency="'EUR' /*customer.currency*/" />
              </b-tab>
              <!-- Customer credits -->
              <b-tab :title="$t('globals.credits')">
                <ulu-customer-credits :customer="customer.id" :total="parseInt(customer.credits_total)"
                  :currency="'EUR' /*customer.currency*/" :refresh="fetchCustomer" />
              </b-tab>
              <!-- Sinatec -->
              <b-tab title="Sinatec">
                <ulu-customer-synatec-orders :customer="customer.id" :total="parseInt(customer.synatec_orders_total)" />
              </b-tab>
              <!-- Customer contract details -->
              <b-tab :title="$t('globals.contract_details')">
                <ulu-customer-contract-details :notes="customer.notes" :refresh="fetchCustomer" />
              </b-tab>
              <!-- Mollie payments, set up  -->
              <b-tab v-if="getUserSystem == 'Cartracker' || getUserSystem == 'traksi'" title="Mollie">
                <ulu-customer-mollie :customer="customer.id" :mollie_payment_order_status="customer.mollie_payment_order_status
                  " :mollie_payments="customer.mollie_payments" :refresh="fetchCustomer"
                  :total="parseInt(customer.mollie_payments_total)" />
              </b-tab>
              <template #tabs-end>
                <b-nav-item href="#" role="presentation" @click.prevent='$bvModal.show("status-modal")'>
                  {{ $t('globals.audits') }}
                </b-nav-item>
              </template>
            </b-tabs>
          </div>
        </div>
      </div>
      <!-- Audits modal -->
      <b-modal id="status-modal" hide-footer centered size="lg">
        <template v-slot:modal-title>
          {{ $t('globals.audits') }}:
        </template>
        <b-row>
          <b-col>
            <b-table class='text-rq-primary-blue' :items='customer.audits' :fields='auditsFields' sort-by='createdAt'
              :sort-desc='true' bordered small>
            </b-table>
          </b-col>
        </b-row>
      </b-modal>
    </template>
  </div>
</template>

<script>
import Customer from "@/models/customer/customer";
import { mapActions, mapMutations, mapGetters } from "vuex";
import systemRestriction from "@/mixins/systemRestriction";

export default {
  mixins: [systemRestriction],
  systemRestrictionReload: "fetchCustomer",
  components: {
    "ulu-customer-header": () =>
      import("@/components/customers/customer/CustomerHeader"),
    "ulu-customer-details": () =>
      import("@/components/customers/customer/CustomerDetails"),
    "ulu-customer-data": () =>
      import("@/components/customers/customer/CustomerData"),
    "ulu-customer-orders-inquiries": () =>
      import("@/components/customers/customer/CustomerOrdersInquiries"),
    "ulu-customer-invoices": () =>
      import("@/components/customers/customer/CustomerInvoices"),
    "ulu-customer-parking-invoices": () =>
      import("@/components/customers/customer/CustomerParkingInvoices"),
    "ulu-customer-credits": () =>
      import("@/components/customers/customer/CustomerCredit"),
    "ulu-customer-contract-details": () =>
      import("@/components/customers/customer/CustomerContractDetails"),
    "ulu-customer-mollie": () =>
      import("@/components/customers/customer/CustomerMollie"),
    "ulu-customer-devices": () =>
      import("@/components/customers/customer/CustomerDevices"),
    "ulu-customer-synatec-orders": () =>
      import("@/components/customers/customer/CustomerSynatecOrders")
  },
  data() {
    return {
      customer: new Customer(),
      auditsFields: [
        {
          key: 'action',
          label: this.$t('globals.action'),
        },
        {
          key: 'agent.full_name',
          label: this.$t('globals.agent'),
        },
        {
          key: 'created_at',
          label: this.$t('globals.created_at'),
          sortable: true,
        },
      ],
      tab: 0,
    };
  },
  methods: {
    ...mapMutations(["setShowLoadingModal"]),
    ...mapActions("axios_mod", ["simpleFetch"]),
    fetchCustomer() {
      const data = {
        query: `
          query {
            Customer(id: ${this.$route.params.id}) {
              notes
              brand
              created_at
              agent {
                full_name
              }
              id
              currency
              web_app_id
              company_name
              company_address
              company_city
              company_postal_code
              company_country
              company_vat
              company_po
              contact_first_name
              contact_last_name
              contact_email
              contact_phone
              contact_address
              contact_city
              contact_postal_code
              contact_country
              shipping_first_name
              shipping_last_name
              shipping_email
              shipping_phone
              shipping_address
              shipping_city
              shipping_postal_code
              shipping_country
              billing_first_name
              billing_last_name
              billing_email
              billing_phone
              billing_address
              billing_city
              billing_postal_code
              billing_country
              block_streatus
              send_to_sharpspring
              customer_type
              category
              source
              webapp_registration_sent_at
              webapp_registration_accepted_at
              customer_settings {
                invoice_imeis
                is_whitelabel
                is_keurmerk
                driver_id
                live_driver_id
                api
                parking
                iframe_page
                sms
                web_app
                company_type
              }
              mollie_payment_order_status
              audits {
                agent {
                  full_name
                }
                action
                created_at
              }
            }
          }
        `
      };

      this.setShowLoadingModal(true);
      this.simpleFetch({ data, this_ref: this })
        .then(res => {
          if (res && this.checkSystem(res.Customer.brand)) {
            this.customer = res.Customer;
          }
        })
        .finally(() => {
          this.setShowLoadingModal(false);
          this.tab = parseInt(this.$route.query.tab) || 0;
        });
    }
  },
  computed: {
    ...mapGetters(["getUserSystem", "getShowLoadingModal"])
  },
  created() {
    this.fetchCustomer();
  },
  watch: {
    tab(n, o) {
      this.$router.replace({ query: { tab: n } }).catch(() => { });
    },
  }
};
</script>

<style scoped>
.customer-page {
  background-color: #f7f7f7;
  min-height: 100vh;
  padding: 24px;
}

/* Main Header */
.main-header {
  background: white;
  padding: 16px 24px;
  margin-bottom: 20px;
  display: flex;
  justify-content: space-between;
  align-items: center;
  gap: 24px;
  border-radius: 12px;
  box-shadow: 0 1px 3px rgba(0, 0, 0, 0.1);
}

.refresh-btn {
  display: flex;
  align-items: center;
  justify-content: center;
  width: 36px;
  height: 36px;
  background-color: #f7fafc;
  border: 1px solid #e2e8f0;
  border-radius: 6px;
  cursor: pointer;
  color: #4a5568;
  transition: all 0.2s;
  flex-shrink: 0;
}

.refresh-btn:hover:not(:disabled) {
  background-color: #edf2f7;
  border-color: #5469d4;
  color: #5469d4;
}

.refresh-btn:disabled {
  opacity: 0.6;
  cursor: not-allowed;
}

/* Content Layout */
.customer-content {
  display: grid;
  grid-template-columns: 200px 1fr;
  gap: 24px;
}

.customer-sidebar {
  background: white;
  padding: 20px;
  height: fit-content;
  border-radius: 12px;
  box-shadow: 0 1px 3px rgba(0, 0, 0, 0.1);
}

.customer-number-container {
  margin-bottom: 24px;
  padding-bottom: 20px;
  border-bottom: 1px solid #e2e8f0;
}

.customer-number {
  font-size: 18px;
  font-weight: 700;
  color: #2d3748;
  margin: 0;
  line-height: 1.3;
}

.customer-main {
  background: white;
  overflow: visible;
  border-radius: 12px;
  box-shadow: 0 1px 3px rgba(0, 0, 0, 0.1);
  /* Lets wide tab tables shrink inside the grid instead of overflowing */
  min-width: 0;
}

.tabs-wrapper >>> .nav-tabs {
  border-bottom: 1px solid #e0e0e0;
  background-color: white;
  padding: 0;
  margin-bottom: 0;
}

.tabs-wrapper >>> .nav-tabs .nav-link {
  border: none;
  border-bottom: 3px solid transparent;
  color: #6c757d;
  font-size: 0.9375rem;
  font-weight: 500;
  padding: 1rem 1.5rem;
  background-color: transparent;
  transition: all 0.2s;
}

.tabs-wrapper >>> .nav-tabs .nav-link:hover {
  border-bottom-color: #ff6b35;
  color: #495057;
}

.tabs-wrapper >>> .nav-tabs .nav-link.active {
  color: #ff6b35;
  border-bottom-color: #ff6b35;
  background-color: white;
  font-weight: 600;
}

.tabs-wrapper >>> .tab-content {
  background-color: white;
  border: none;
  padding: 24px;
}

@media (max-width: 1024px) {
  .main-header {
    flex-wrap: wrap;
  }

  .customer-content {
    grid-template-columns: 1fr;
  }

  .customer-sidebar {
    order: 2;
  }

  .customer-main {
    order: 1;
  }
}

@media (max-width: 768px) {
  .customer-page {
    padding: 16px;
  }

  .main-header {
    padding: 16px;
    flex-direction: column;
    align-items: flex-start;
  }

  .customer-number {
    font-size: 16px;
  }

  .tabs-wrapper >>> .nav-tabs .nav-link {
    padding: 0.75rem 1rem;
    font-size: 0.875rem;
  }

  .tabs-wrapper >>> .tab-content {
    padding: 16px;
  }
}
</style>